import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { logger } from "@/infrastructure/services/LoggerService";
import { useDonationStore } from "../../stores/useDonationStore";
/**
 * Donation form/select options.
 *
 * Catalog-backed lists (donationType, paymentMethod, status, donationForm,
 * donationNature, donationSource, donationFrequency, donorType) are read from
 * `useDonationStore().catalogs` — single source of truth provided by the BFF
 * `GET /donations/catalogs` endpoint. Lists are empty until the catalog
 * resolves; the composable triggers `loadCatalogs()` lazily (idempotent).
 *
 * The remaining lists (subType, acquisitionChannel, currency) have no BFF
 * catalog equivalent yet and stay hardcoded.
 */
export function useDonationOptions() {
    const { t } = useI18n();
    const store = useDonationStore();
    // Lazy fetch — store guards against duplicate calls. The store also sets its
    // own `error` ref and the modal surfaces a toast on failure; this composable
    // is invoked from views (e.g. detail tabs) that don't own toast plumbing,
    // so we log the rejection here per R47 instead of swallowing it silently.
    void store
        .loadCatalogs()
        .catch((err) => logger.error("[useDonationOptions] loadCatalogs failed:", err));
    function toOptions(entries) {
        return (entries ?? []).map((e) => ({ value: e.code, label: t(e.label) }));
    }
    // The BFF catalog leaves these out so nobody picks them by hand, but the public donation form
    // records them: a record holding one must still find its label, or its select renders blank.
    const SYSTEM_PAYMENT_METHODS = ["Online"];
    function systemPaymentMethodOptions() {
        return SYSTEM_PAYMENT_METHODS.map((code) => ({
            value: code,
            label: t(`finance.donations.paymentMethod.${code}`),
        }));
    }
    /** True for a method only a system flow writes (never offered by the catalog). */
    function isSystemPaymentMethod(code) {
        return !!code && SYSTEM_PAYMENT_METHODS.includes(code);
    }
    /**
     * A donation's system-written method is read-only only when OPAY confirmed the payment; a legacy
     * or unconfirmed one stays correctable by hand (e.g. from a bank statement).
     */
    function isDonationPaymentMethodLocked(method, provider) {
        return isSystemPaymentMethod(method) && provider === "Opay";
    }
    /**
     * Payment-method options for one record's select: the catalog, plus any system-only value among
     * `held`, so its label displays. Pass the persisted value next to the edited one on an editable
     * select: keyed on the edited value alone, picking another method drops the system one and the
     * user cannot switch back before saving.
     */
    function paymentMethodOptionsFor(held) {
        return computed(() => {
            const options = toOptions(store.catalogs?.paymentMethod);
            const values = held();
            const system = systemPaymentMethodOptions().filter((o) => values.includes(o.value) && !options.some((c) => c.value === o.value));
            return [...options, ...system];
        });
    }
    // ── Frontend-only lists (no BFF catalog equivalent) ────────────────────────
    // subType is currently a UI-only sub-filter; not yet promoted to BFF catalog.
    const subTypeOptions = [
        { value: "OneOff", label: t("finance.donations.type.OneOff") },
        { value: "Regular", label: t("finance.donations.type.Regular") },
    ];
    const acquisitionChannelOptions = [
        { value: "SocialMedia", label: t("finance.donations.detail.channel.SocialMedia") },
        { value: "Email", label: t("finance.donations.detail.channel.Email") },
        { value: "Event", label: t("finance.donations.detail.channel.Event") },
        { value: "Website", label: t("finance.donations.detail.channel.Website") },
        { value: "Phone", label: t("finance.donations.detail.channel.Phone") },
        { value: "Direct", label: t("finance.donations.detail.channel.Direct") },
    ];
    // Do NOT wrap this object in `reactive(...)`. Reactive auto-unwraps computed
    // refs on access, so destructuring the return value would capture an empty-
    // array snapshot before `loadCatalogs()` resolves and never update again.
    // Returning refs directly keeps destructured locals reactive (templates
    // auto-unwrap top-level refs in <script setup>).
    return {
        donorTypeOptions: computed(() => toOptions(store.catalogs?.donorType)),
        donationTypeOptions: computed(() => toOptions(store.catalogs?.donationType)),
        paymentMethodOptions: computed(() => toOptions(store.catalogs?.paymentMethod)),
        // Labels only (history, audit values): never bind it to a select, it offers the system values.
        paymentMethodLabelOptions: computed(() => [
            ...toOptions(store.catalogs?.paymentMethod),
            ...systemPaymentMethodOptions(),
        ]),
        paymentMethodOptionsFor,
        isSystemPaymentMethod,
        isDonationPaymentMethodLocked,
        statusOptions: computed(() => toOptions(store.catalogs?.transactionStatus)),
        donationFormOptions: computed(() => toOptions(store.catalogs?.donationForm)),
        donationNatureOptions: computed(() => toOptions(store.catalogs?.donationNature)),
        donationSourceOptions: computed(() => toOptions(store.catalogs?.donationSource)),
        donationFrequencyOptions: computed(() => toOptions(store.catalogs?.subscriptionFrequency)),
        subTypeOptions,
        acquisitionChannelOptions,
    };
}
